<script setup lang="ts">
import type { ConsentState } from '~/composables/useCookieConsent'

const { t } = useI18n()
const { consent, savePreferences } = useCookieConsent()

const model = defineModel<boolean>({ default: false })

const draft = reactive<ConsentState>({
  essential: true,
  analytics: consent.value?.analytics ?? false,
  marketing: consent.value?.marketing ?? false,
})

// Sync draft when modal opens
watch(model, (open) => {
  if (open) {
    draft.analytics = consent.value?.analytics ?? false
    draft.marketing = consent.value?.marketing ?? false
  }
})

function save() {
  savePreferences({ ...draft })
  model.value = false
}

const categories = computed(() => [
  {
    key: 'essential' as const,
    label: t('cookiePreferences.essential.label'),
    description: t('cookiePreferences.essential.description'),
    required: true,
  },
  {
    key: 'analytics' as const,
    label: t('cookiePreferences.analytics.label'),
    description: t('cookiePreferences.analytics.description'),
    required: false,
  },
  {
    key: 'marketing' as const,
    label: t('cookiePreferences.marketing.label'),
    description: t('cookiePreferences.marketing.description'),
    required: false,
  },
])
</script>

<template>
  <UModal :open="model" :title="t('cookiePreferences.title')" @update:open="model = $event">
    <template #body>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-5">
        {{ t('cookiePreferences.description') }}
      </p>

      <div class="space-y-4">
        <div
          v-for="cat in categories"
          :key="cat.key"
          class="flex items-start justify-between gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50"
        >
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ cat.label }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ cat.description }}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="String(cat.required || draft[cat.key])"
            :aria-label="cat.label"
            :disabled="cat.required"
            :class="[
              'relative inline-flex h-5 w-9 shrink-0 mt-0.5 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
              (cat.required || draft[cat.key]) ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600',
              cat.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
            ]"
            @click="() => { if (!cat.required) draft[cat.key] = !draft[cat.key] }"
          >
            <span
              :class="[
                'pointer-events-none inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200',
                (cat.required || draft[cat.key]) ? 'translate-x-4' : 'translate-x-0.5'
              ]"
            />
          </button>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end gap-3 w-full">
        <button
          class="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          @click="model = false"
        >
          {{ t('cookiePreferences.cancel') }}
        </button>
        <button
          class="px-5 py-2.5 text-sm font-semibold rounded-xl bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          @click="save"
        >
          {{ t('cookiePreferences.save') }}
        </button>
      </div>
    </template>
  </UModal>
</template>
